// ============================================================================
// HAI Reporting Tool - Protected Route Component
// Requires authentication and optional role-based access to routes
// ============================================================================

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from '@/types';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole; // Optional: restrict to specific role
  adminOnly?: boolean; // Optional: restrict to Admin only
}

export function ProtectedRoute({ children, requiredRole, adminOnly = false }: ProtectedRouteProps) {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  // Authenticated but no profile loaded yet
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Check for admin-only access
  if (adminOnly && userProfile.role !== 'Admin') {
    // Non-admin trying to access admin route - redirect to entry page
    return <Navigate to="/entry" replace />;
  }

  // Check for specific required role
  if (requiredRole && userProfile.role !== requiredRole) {
    // User doesn't have required role - redirect based on their actual role
    if (userProfile.role === 'Admin') {
      return <Navigate to="/admin/dashboard" replace />;
    } else {
      return <Navigate to="/entry" replace />;
    }
  }

  return <>{children}</>;
}
